import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Unlock, Lock, Play, ExternalLink } from 'lucide-react';
import { useAccount } from 'wagmi';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';
import { debugLog, debugWarn, debugInfo, debugError } from '../utils/debug';
import './UnlockedTestimoniesModal.css';

const UnlockedConfessionsModal = ({ isOpen, onClose }) => {
  const [unlockedConfessions, setUnlockedConfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { address, isConnected } = useAccount();

  useEffect(() => {
    if (isOpen && isConnected && address) {
      checkUnlockedConfessions();
    }
  }, [isOpen, isConnected, address]);

  const checkUnlockedConfessions = async () => {
    try {
      setLoading(true);
      debugLog('🔍 Checking unlocked confessions for wallet:', address);

      // Get all confessions
      const response = await fetch(`${getApiBaseUrl()}/confessions`);
      if (!response.ok) throw new Error('Failed to fetch confessions');
      
      const confessions = await response.json();
      debugLog('📝 Total confessions:', confessions.length);

      // Get wallet's token balances from Base Mainnet
      const apiUrl = `https://base.blockscout.com/api/v2/addresses/${address}/token-balances`;
      const balanceResponse = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      let walletTokens = [];
      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        walletTokens = balanceData
          .filter(token => token.token && parseFloat(token.value) > 0)
          .map(token => token.token.address.toLowerCase());
        
        debugLog('💰 Wallet tokens:', walletTokens);
      }

      // Check which confessions user has access to
      const unlocked = confessions.filter(confession => 
        confession.nftContractAddress && 
        walletTokens.includes(confession.nftContractAddress.toLowerCase())
      );

      debugLog('🔓 Unlocked confessions:', unlocked.length);
      setUnlockedConfessions(unlocked);
      
    } catch (error) {
      debugError('Error checking unlocked confessions:', error);
      toast.error('Failed to check unlocked confessions');
      setUnlockedConfessions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="unlocked-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="unlocked-modal"
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>
              <Unlock size={24} />
              Unlocked Confessions
            </h2>
            <button className="modal-close" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="loading-state">
                <div className="spinner large"></div>
                <p>Checking your token ownership...</p>
              </div>
            ) : unlockedConfessions.length === 0 ? (
              <div className="empty-state">
                <Lock size={48} className="empty-icon" />
                <h3>No Unlocked Confessions</h3>
                <p>You don't currently own tokens for any confessions. Purchase tokens to unlock full audio access.</p>
              </div>
            ) : (
              <div className="unlocked-list">
                <p className="list-header">
                  You have access to <strong>{unlockedConfessions.length}</strong> full confessions:
                </p>
                
                {unlockedConfessions.map((confession) => (
                  <div key={confession.id} className="unlocked-item">
                    <div className="item-content">
                      <div className="item-header">
                        <Unlock size={16} className="unlock-icon" />
                        <span className="item-date">{formatDate(confession.createdAt)}</span>
                      </div>
                      
                      <p className="item-text">{confession.textContent}</p>
                      
                      <div className="item-meta">
                        <div className="audio-duration">
                          <Play size={14} />
                          <span>{confession.audioDuration}s full audio</span>
                        </div>
                        
                        <div className="supported-ngos">
                          {confession.ngos && confession.ngos.map((ngo, index) => (
                            <span key={ngo.id} className="ngo-chip">
                              {ngo.name}
                              {index < confession.ngos.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="item-actions">
                      <button 
                        className="btn btn-small btn-primary"
                        onClick={() => {
                          // Scroll to confessions section and close modal
                          document.getElementById('confessions')?.scrollIntoView({ behavior: 'smooth' });
                          onClose();
                        }}
                      >
                        <ExternalLink size={14} />
                        View in Gallery
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UnlockedConfessionsModal;